import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import LinkIcon from "components/svg-icons/link-icon";
import { useCIQLazyQuery } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import CiqAgSearch from "components/ciq-ag-search";
import { Button, Dropdown, Space, Tooltip } from "antd";
import { ProjectContext } from "context/ProjectProvider";
import { useHistory, useLocation } from "react-router";
import { ErrorMessages } from "constants/index";
import { specSectionLabel } from "components/cell-renders";
import { mergedStrings } from "utils/utils";
import { ENTITIES_DROPDOWN_NAMES } from "../queries/queries";
import { DropDownDBFeatureInstance, TFeatureDetail } from "../models";
import VirtualList from "./virtual-list";

// Lightweight display-only type — no instance reference
type DropdownDisplayItem = {
  id: string;
  pre: string;
  title: string;
  isLinked: boolean;
};

// Lightweight transform — only display fields, no instance embedding
function toDisplayItems(
  data: DropDownDBFeatureInstance[]
): DropdownDisplayItem[] {
  return data.map((item) => {
    const specSection = item.feature_instance_spec_sections[0]?.spec_section;
    const specSectionStr = specSectionLabel({
      specNumber: specSection?.section_number,
      specName: specSection?.title
    });
    const pre = mergedStrings([
      String(item.auto_inc_value || ""),
      specSectionStr
    ]);
    return {
      id: item.id,
      pre,
      title: item.title || "",
      isLinked:
        item.links_as_source_aggregate.aggregate.count > 0 ||
        item.links_as_target_aggregate.aggregate.count > 0
    };
  });
}

function DropDownEntityNames({
  featureDetail,
  selectedDropdownEntity,
  setSelectedDropdownEntity,
  disable
}: Readonly<{
  featureDetail: TFeatureDetail;
  selectedDropdownEntity: DropdownDisplayItem | undefined;
  setSelectedDropdownEntity: (entity: DropdownDisplayItem) => void;
  disable: boolean;
}>) {
  const { search } = useLocation();
  const history = useHistory();
  const location = useLocation();
  const { gqlClientForProject } = useContext(ProjectContext);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Single source of truth — raw response stored once, display items derived via useMemo
  const [rawData, setRawData] = useState<DropDownDBFeatureInstance[]>([]);

  const [fetchEntities] = useCIQLazyQuery(ENTITIES_DROPDOWN_NAMES);

  // Fetch once on mount — bypasses Apollo entirely (raw HTTP POST, no cache)
  useEffect(() => {
    if (!gqlClientForProject) return;
    let cancelled = false;
    setLoading(true);
    fetchEntities({
      feature_type_id: featureDetail.featureTypeId,
      excluded_instance_id: []
    }).then((response) => {
      if (cancelled) return;
      const items: DropDownDBFeatureInstance[] =
        response?.data?.data?.feature_instance || [];
      setRawData(items);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchEntities, gqlClientForProject, featureDetail.featureTypeId]);

  // Derive lightweight display items — no separate state copy
  const entities = useMemo(() => toDisplayItems(rawData), [rawData]);

  // Search index — flat Map for O(1) per-item lookup
  const searchIndex = useMemo(() => {
    const idx = new Map<string, string>();
    entities.forEach((item) => {
      idx.set(item.id, `${item.pre} ${item.title}`.toLowerCase());
    });
    return idx;
  }, [entities]);

  // Auto-select entity from URL params
  useEffect(() => {
    if (entities?.length) {
      try {
        const urlParams = new URLSearchParams(search);
        const entityId = urlParams.get("entityId");
        const materialId = urlParams.get("materialId");
        const submittalId = urlParams.get("SubmittalId");

        const id = entityId || materialId || submittalId;
        if (id) {
          const obj = entities.find((item) => item.id === id);
          if (obj) setSelectedDropdownEntity(obj);
        }
      } catch (e) {
        console.error("Error parsing entityId from URL", e);
      }
    }
  }, [entities, search, setSelectedDropdownEntity]);

  // CiqAgSearch owns the live input text and debounces (500ms) before calling
  // back, so this no longer needs a timer of its own. `searchText` is now just
  // the committed value fed back to the box so onItemClick can clear it.
  const onSearchChange = useCallback((val: string) => {
    setSearchText(val);
    setDebouncedSearch(val.toLowerCase().trim());
  }, []);

  const filteredEntities = useMemo(() => {
    if (!debouncedSearch) return entities;
    return entities.filter((item) => {
      const label = searchIndex.get(item.id);
      // searchIndex has an entry (always a non-empty string) for every entities id, and filteredEntities iterates that same entities array, so label is never falsy — the `: false` branch is unreachable
      /* v8 ignore next */
      return label ? label.includes(debouncedSearch) : false;
    });
  }, [entities, debouncedSearch, searchIndex]);

  const onItemClick = useCallback(
    (itemId: string) => {
      setOpen(false);
      setSearchText("");
      setDebouncedSearch("");
      history.replace(`${location.pathname}?entityId=${itemId}`);
    },
    [history, location.pathname]
  );

  const renderDropdownItem = useCallback(
    (item: DropdownDisplayItem) => (
      <Space
        className="linking-container--nav__option flex items-center justify-between"
        onClick={() => onItemClick(item.id)}
        data-testid={`dropdownentitynames-option-${item.id}`}
      >
        <Tooltip
          title={
            <div className="space-y-1 py-1">
              <i className="text-ciq-gray-950 text-opacity-80 entity-id-with-spec">
                {item.pre}
              </i>
              <div>{item.title}</div>
            </div>
          }
          mouseEnterDelay={0.5}
        >
          {/* Single flex child: antd v6 only keeps the child's own className,
              so the flex+width container must live on the element itself. */}
          <div className="flex items-center w-[410px]">
            <div className="text-left grow items-start justify-start space-x-2 truncate block">
              <i className="entity-id-with-spec">{item.pre}</i>
              <span>{item.title}</span>
            </div>
            {item.isLinked && (
              <span className="size-4 flex items-center justify-center">
                <LinkIcon />
              </span>
            )}
          </div>
        </Tooltip>
      </Space>
    ),
    [onItemClick]
  );

  const selectedEntityUI = useMemo(() => {
    if (!selectedDropdownEntity) return null;
    const item = selectedDropdownEntity;
    return (
      <Tooltip
        title={
          disable ? null : (
            <div className="space-y-1 py-1">
              <i className="text-ciq-gray-950 text-opacity-80 entity-id-with-spec">
                {item.pre}
              </i>
              <div>{item.title}</div>
            </div>
          )
        }
      >
        {/* Single flex child: antd v6 only keeps the child's own className,
            so the flex+width container must live on the element itself. */}
        <div
          className="flex items-center grow min-w-0"
          data-testid={`dropdownentitynames-tooltip-${item.id}`}
        >
          <div className="text-left grow min-w-0 items-start justify-start space-x-2 truncate block">
            <i className="entity-id-with-spec">{item.pre}</i>
            <span>{item.title}</span>
          </div>
          {item.isLinked && (
            <span className="size-4 flex items-center justify-center">
              <LinkIcon />
            </span>
          )}
        </div>
      </Tooltip>
    );
  }, [selectedDropdownEntity, disable]);

  const overlayContent = useMemo(() => {
    if (loading) {
      return (
        <div
          className="linking-container--nav__loading h-20"
          data-testid="dropdownentitynames-loading"
        >
          <LoadingOutlined />
        </div>
      );
    }
    if (!entities.length) {
      return (
        <div
          className="linking-container--nav__loading h-20"
          data-testid="dropdownentitynames-no-data"
        >
          No {featureDetail.title} Available
        </div>
      );
    }

    return (
      <div className="linking-container--nav__dropdown">
        <div className="linking-container--nav__dropdown--search">
          <CiqAgSearch
            className="w-full"
            placeholder={`Search ${featureDetail.title} Here`}
            value={searchText}
            onSearch={onSearchChange}
            data-testid="dropdownentitynames-search"
          />
        </div>
        <VirtualList
          items={filteredEntities}
          itemHeight={36}
          style={{ height: Math.min(360, filteredEntities.length * 36) || 36 }}
          renderItem={renderDropdownItem}
        />
      </div>
    );
  }, [
    entities.length,
    featureDetail.title,
    filteredEntities,
    loading,
    onSearchChange,
    renderDropdownItem,
    searchText
  ]);

  return (
    <Tooltip title={disable ? ErrorMessages.EditingLinkingMessage : null}>
      <Dropdown
        menu={{ items: [] }}
        popupRender={() => overlayContent}
        className="linking-container--nav__select"
        trigger={["click"]}
        open={open}
        onOpenChange={(op) => {
          setOpen(op);
        }}
        disabled={disable}
        data-testid="dropdownentitynames-dropdown"
      >
        {/* mx-4 lives on the trigger Button: antd v6 Dropdown no longer
            forwards its own className to the trigger child (it goes to the
            popup), so the side gaps to the title and the detail button must be
            set on the Button itself. */}
        <Button className="mx-4" data-testid="dropdownentitynames-button">
          {selectedDropdownEntity ? (
            // Plain flex row (was antd <Space>, whose v6 gap model was the
            // problem). The content grows and shrinks (min-w-0 lets the text
            // truncate); gap-2 reproduces the old ~8px spacing before the
            // chevron, which keeps a fixed size.
            <div className="w-[425px] flex items-center gap-2">
              {selectedEntityUI}
              <DownOutlined className="shrink-0" />
            </div>
          ) : (
            <div
              className="w-[425px] flex justify-between items-center"
              data-testid="dropdownentitynames-select-a-entity"
            >
              <div>Select a {featureDetail.title}</div>
              <DownOutlined />
            </div>
          )}
        </Button>
      </Dropdown>
    </Tooltip>
  );
}
export default DropDownEntityNames;
